import React from 'react';
import SectionHeader from './SectionHeader';
import NotesInput from './NotesInput';
import CurrencyInput from './CurrencyInput';

const EMPTY_TUITION = { description: '', amount: null, years_remaining: '' };

export default function StepExpenses({ data, onChange }) {
  const value = data || {};

  function update(field, val) {
    onChange({ ...value, [field]: val });
  }

  // Tuition entries list
  const tuitionEntries = (value.tuition_entries && value.tuition_entries.length > 0)
    ? value.tuition_entries
    : [{ ...EMPTY_TUITION }];

  function updateTuition(idx, field, val) {
    const next = tuitionEntries.map((e, i) => (i === idx ? { ...e, [field]: val } : e));
    update('tuition_entries', next);
  }

  function addTuition() {
    update('tuition_entries', [...tuitionEntries, { ...EMPTY_TUITION }]);
  }

  function removeTuition(idx) {
    const next = tuitionEntries.filter((_, i) => i !== idx);
    update('tuition_entries', next.length > 0 ? next : [{ ...EMPTY_TUITION }]);
  }

  return (
    <div>
      <SectionHeader
        number={8}
        title="New / Planned Expenses"
        tip="We are targeting these areas because of a unique timeline, inflation factors, or special reason."
        showAnnualNote={false}
      />

      {/* Rent */}
      <div className="cashflow-section-card">
        <h6 className="cashflow-subsection-title">Rent Expenses</h6>
        <div className="mb-3">
          <label className="form-label">Do you currently rent your home?</label>
          <div>
            {['yes', 'no'].map((opt) => (
              <div className="form-check form-check-inline" key={opt}>
                <input
                  className="form-check-input"
                  type="radio"
                  id={`rents_${opt}`}
                  name="rents"
                  checked={value.rents === opt}
                  onChange={() => update('rents', opt)}
                />
                <label className="form-check-label" htmlFor={`rents_${opt}`}>
                  {opt === 'yes' ? 'Yes' : 'No'}
                </label>
              </div>
            ))}
          </div>
        </div>
        {value.rents === 'yes' && (
          <div className="row g-2">
            <div className="col-md-5">
              <CurrencyInput
                label="Monthly rent cost"
                id="rent_amount"
                value={value.rent_amount}
                onChange={(v) => update('rent_amount', v)}
                defaultMonthly
              />
            </div>
          </div>
        )}
      </div>

      {/* Tuition */}
      <div className="cashflow-section-card">
        <h6 className="cashflow-subsection-title">Tuition</h6>
        <div className="mb-3">
          <label className="form-label">Do you have any tuition expenses?</label>
          <div>
            {['yes', 'no'].map((opt) => (
              <div className="form-check form-check-inline" key={opt}>
                <input
                  className="form-check-input"
                  type="radio"
                  id={`has_tuition_${opt}`}
                  name="has_tuition"
                  checked={value.has_tuition === opt}
                  onChange={() => update('has_tuition', opt)}
                />
                <label className="form-check-label" htmlFor={`has_tuition_${opt}`}>
                  {opt === 'yes' ? 'Yes' : 'No'}
                </label>
              </div>
            ))}
          </div>
        </div>
        {value.has_tuition === 'yes' && (
          <>
            {tuitionEntries.map((entry, idx) => (
              <div key={idx} className="border rounded p-3 mb-2 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong className="small">Student #{idx + 1}</strong>
                  {tuitionEntries.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0"
                      onClick={() => removeTuition(idx)}
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                  )}
                </div>
                <div className="row g-2">
                  <div className="col-md-4">
                    <label className="form-label">Student name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Emma"
                      value={entry.description || ''}
                      onChange={(e) => updateTuition(idx, 'description', e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <CurrencyInput
                      label="Annual amount"
                      id={`tuition_amount_${idx}`}
                      value={entry.amount}
                      onChange={(v) => updateTuition(idx, 'amount', v)}
                      noToggle
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Years remaining</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. 3"
                      value={entry.years_remaining || ''}
                      onChange={(e) => updateTuition(idx, 'years_remaining', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm mb-2"
              onClick={addTuition}
            >
              <i className="bi bi-plus-lg me-1" />
              Add another student
            </button>
          </>
        )}
      </div>

      {/* Medical / Healthcare */}
      <div className="cashflow-section-card">
        <h6 className="cashflow-subsection-title">Medical / Healthcare</h6>
        <div className="row g-2">
          <div className="col-md-5">
            <CurrencyInput
              label="Estimated annual out-of-pocket medical expenses"
              id="medical_annual"
              value={value.medical_annual}
              onChange={(v) => update('medical_annual', v)}
              noToggle
            />
          </div>
        </div>
      </div>

      <NotesInput
        value={value.notes}
        onChange={(v) => update('notes', v)}
      />
    </div>
  );
}
